import React from "react";
import { cn } from "../../shared/utils/cn";
import Input, { type InputProps } from "./Input";

export interface TextFieldProps extends InputProps {
  inputLeftAddon?: React.ReactNode;
  inputRightAddon?: React.ReactNode;
  error?: string;
  width?: any;
  label?: string;
  resize?: boolean;
  oneLine?: boolean;
  hideErrorMessage?: boolean;
  leftAddonPadding?: string;
  rightAddonPadding?: string;
  rows?: string;
  whiteSpace?: any;
  onClickRightAddon?: () => void;
  isRequired?: boolean;
  marginBottom?: string;
  isRightAddonClickable?: boolean;
  noFocusRing?: boolean;
  borderless?: boolean;
  inputClassName?:string;
}

const Textfield = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      leftAddonPadding = "0.81rem 1.25rem",
      rightAddonPadding = "0.81rem 1.25rem",
      hideErrorMessage = false,
      oneLine = false,
      resize = true,
      as: Component = "input",
      rows = 20,
      label,
      error,
      inputLeftAddon,
      inputRightAddon,
      onClickRightAddon,
      width = "100%",
      whiteSpace,
      isRequired,
      isRightAddonClickable = false,
      className,
      noFocusRing = false,
      borderless = false,
      inputClassName,
      ...props
    },
    ref
  ) => {
    const fieldClassName = cn(
      "w-full p-[0.85rem_0.5rem]! shadow-none! placeholder:text-[rgba(117,117,117,0.8)] focus:shadow-none!",
      borderless && "border-0! border-none! focus:border-0!",
      noFocusRing &&
        "focus:shadow-none! focus:outline-none! focus-visible:outline-none!",
      inputLeftAddon && "rounded-l-none! border-l-0",
      inputRightAddon && "rounded-r-none!",
      resize ? "resize-y" : "resize-none", inputClassName
    );

    const handleEnterPrevent = (
      e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (e.code === "Enter") e.preventDefault();
    };

    return (
      <div
        className={cn(
          "flex w-full items-start md:items-stretch",
          oneLine ? "flex-row md:flex-col" : "flex-col",
          className
        )}
        style={{ whiteSpace, width }}
      >
        {label && (
          <div
            className={cn(
              "md:mb-[1rem]",
              isRequired ? "font-bold" : "font-semibold",
              oneLine ? "mr-[1rem]" : "mb-[1rem]"
            )}
          >
            {`${label} ${isRequired ? " *" : ""}`}
          </div>
        )}
        <div
          className={cn(
            "flex w-full rounded-[0.5rem] transition-all",
            !noFocusRing &&
              "focus-within:shadow-[0_0_0_0.3rem_rgba(59,130,246,0.25)]",
            error ? "shadow-btn-focus-danger" : ""
          )}
        >
          {inputLeftAddon && (
            <div
              className={cn(
                "flex items-center rounded-l-[0.5rem] bg-grey-secondary",
                !borderless && "border-[0.1rem] border-solid border-light/70"
              )}
              style={{
                padding: leftAddonPadding,
              }}
            >
              {inputLeftAddon}
            </div>
          )}
          <Input
            as={Component}
            onKeyDown={handleEnterPrevent}
            {...(Component === "textarea" ? { rows: Number(rows) || 20 } : {})}
            {...props}
            ref={ref}
            className={fieldClassName}
          />
          {inputRightAddon && (
            <div
              className={cn(
                "flex items-start rounded-r-[0.5rem] bg-grey-secondary -ml-[0.2rem] -mt-[0.05rem]",
                !borderless && "border-[0.1rem] border-solid border-light/70",
                isRightAddonClickable ? "cursor-pointer" : "cursor-default"
              )}
              style={{
                padding: rightAddonPadding,
              }}
              onClick={onClickRightAddon}
            >
              {inputRightAddon}
            </div>
          )}
        </div>
        {!hideErrorMessage && error && (
          <div className="mt-[1rem] text-small text-left text-error">
            {error}
          </div>
        )}
      </div>
    );
  }
);

export default Textfield;
